import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import AnnouncementCenterEditModal from './AnnouncementCenterEditModal';
import {
  ANNOUNCEMENT_STATE,
  ANNOUNCEMENT_STATE_OPTIONS,
  buildAnnouncementPageParams,
  formatAnnouncementState,
} from './announcementCenterConstants';

const EMPTY_FILTERS = {
  state: '',
  title: '',
};

const TABLE_COLUMNS = ['標題（英語）', '狀態', '創建時間', '順序', '操作'];

export default function AnnouncementCenterPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [offShelfTarget, setOffShelfTarget] = useState(null);
  const [onShelfTarget, setOnShelfTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.announcementCenterPageQuery(buildAnnouncementPageParams(pageNo, nextFilters));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    setMessage('');
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setMessage('');
    loadRecords(1, reset);
  }

  function openCreate() {
    setEditId(null);
    setEditOpen(true);
    setMessage('');
  }

  function openEdit(row) {
    setEditId(row.id);
    setEditOpen(true);
    setMessage('');
  }

  async function confirmDelete() {
    if (!deleteTarget?.id) {
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      await adminApi.deleteAnnouncementCenter(deleteTarget.id);
      setMessage('刪除成功');
      setDeleteTarget(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '刪除失敗');
    } finally {
      setActionLoading(false);
    }
  }

  async function confirmOffShelf() {
    if (!offShelfTarget?.id) {
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      await adminApi.updateAnnouncementCenterState({ id: offShelfTarget.id, state: ANNOUNCEMENT_STATE.OFF });
      setMessage('已下架');
      setOffShelfTarget(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '操作失敗');
    } finally {
      setActionLoading(false);
    }
  }

  async function confirmOnShelf() {
    if (!onShelfTarget?.id) {
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      await adminApi.updateAnnouncementCenterState({ id: onShelfTarget.id, state: ANNOUNCEMENT_STATE.ON });
      setMessage('已發布');
      setOnShelfTarget(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '操作失敗');
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>公告中心</h2>
        <div className="section-header__actions">
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            新增
          </button>
        </div>
      </div>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="announcementFilterState">狀態</label>
          <select
            id="announcementFilterState"
            value={filters.state}
            onChange={(e) => updateFilter('state', e.target.value)}
          >
            {ANNOUNCEMENT_STATE_OPTIONS.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="announcementFilterTitle">標題</label>
          <input
            id="announcementFilterTitle"
            type="text"
            value={filters.title}
            onChange={(e) => updateFilter('title', e.target.value)}
            placeholder="請輸入公告中心標題"
          />
        </div>
        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜尋
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
        </div>
      </form>

      {message ? <p className="form-message form-message--success">{message}</p> : null}
      {error ? <p className="form-message form-message--error">{error}</p> : null}

      <div className="table-meta">共 {total} 筆</div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  載入中…
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  無資料
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td className="cell-wrap">{row.enTitle || '未設定'}</td>
                  <td>
                    <span className={row.state === ANNOUNCEMENT_STATE.ON ? 'status-text status-text--success' : 'status-text status-text--danger'}>
                      {formatAnnouncementState(row.state)}
                    </span>
                  </td>
                  <td>{formatDateTime(row.createTime)}</td>
                  <td>{row.sort ?? '—'}</td>
                  <td>
                    <div className="table-actions table-actions--news">
                      <div className="table-actions__row">
                        <button type="button" className="btn btn-success btn-sm" onClick={() => openEdit(row)}>
                          修改
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger-outline btn-sm"
                          onClick={() => setDeleteTarget(row)}
                        >
                          刪除
                        </button>
                      </div>
                      <div className="table-actions__row">
                        {row.state === ANNOUNCEMENT_STATE.ON ? (
                          <button
                            type="button"
                            className="btn btn-outline-neutral btn-sm btn-with-icon"
                            onClick={() => setOffShelfTarget(row)}
                          >
                            <span className="btn-state-icon btn-state-icon--danger" aria-hidden="true">
                              ✕
                            </span>
                            下架
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-outline-neutral btn-sm btn-with-icon"
                            onClick={() => setOnShelfTarget(row)}
                          >
                            <span className="btn-state-icon btn-state-icon--success" aria-hidden="true">
                              ✓
                            </span>
                            發布
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(next) => loadRecords(next, filters)} />

      <AnnouncementCenterEditModal
        open={editOpen}
        announcementId={editId}
        onClose={() => setEditOpen(false)}
        onSaved={(wasEdit) => {
          setMessage(wasEdit ? '修改成功' : '新增成功');
          loadRecords(page, filters);
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="刪除公告"
        message="確定刪除此公告？"
        confirmLabel={actionLoading ? '刪除中…' : '刪除'}
        confirmDisabled={actionLoading}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(offShelfTarget)}
        title="下架公告"
        message="確定下架此公告？"
        confirmLabel={actionLoading ? '處理中…' : '下架'}
        confirmDisabled={actionLoading}
        onConfirm={confirmOffShelf}
        onCancel={() => setOffShelfTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(onShelfTarget)}
        title="發布公告"
        message="確定發布此公告？"
        confirmLabel={actionLoading ? '處理中…' : '發布'}
        confirmDisabled={actionLoading}
        onConfirm={confirmOnShelf}
        onCancel={() => setOnShelfTarget(null)}
      />
    </div>
  );
}
